'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { LayoutGroup, motion } from 'framer-motion';
import React, { forwardRef, useId } from 'react';
import { TouchTarget } from './button';
import { Link } from './link';

// Add types for each component
type NavbarProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'nav'>;

type NavbarDividerProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type NavbarSectionProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type NavbarSpacerProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

type NavbarItemProps = {
  current?: boolean;
  className?: string;
  children: React.ReactNode;
} & (
  | Omit<Headless.ButtonProps, 'as' | 'className'>
  | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'className'>
);

type NavbarLabelProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'span'>;

// Update component definitions to use new types
export function Navbar({ className, ...props }: NavbarProps) {
  return (
    <nav
      {...props}
      className={clsx(className, 'tw:flex tw:flex-1 tw:items-center tw:gap-4 tw:py-2.5')}
    />
  );
}

export function NavbarDivider({ className, ...props }: NavbarDividerProps) {
  return (
    <div
      aria-hidden="true"
      {...props}
      className={clsx(className, 'tw:h-6 tw:w-px tw:bg-zinc-950/10 tw:dark:bg-white/10')}
    />
  );
}

export function NavbarSection({ className, ...props }: NavbarSectionProps) {
  const id = useId();
  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'tw:flex tw:items-center tw:gap-3')} />
    </LayoutGroup>
  );
}

export function NavbarSpacer({ className, ...props }: NavbarSpacerProps) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'tw:-ml-4 tw:flex-1')} />;
}

export const NavbarItem = forwardRef(function NavbarItem(
  { current, className, children, ...props }: NavbarItemProps,
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = clsx(
    // Base
    'tw:relative tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:!rounded-lg tw:p-2 tw:text-left tw:text-base/6 tw:font-medium tw:text-brand-green-0 tw:sm:text-sm/5',
    // Leading icon/icon-only
    'tw:data-[slot=icon]:*:size-6 tw:data-[slot=icon]:*:shrink-0 tw:data-[slot=icon]:*:fill-zinc-500 tw:sm:data-[slot=icon]:*:size-5',
    // Trailing icon (down chevron or similar)
    'tw:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:ml-auto tw:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-5 tw:sm:data-[slot=icon]:last:[&:not(:nth-child(2))]:*:size-4',
    // Avatar
    'tw:data-[slot=avatar]:*:-m-0.5 tw:data-[slot=avatar]:*:size-7 tw:data-[slot=avatar]:*:[--avatar-radius:theme(borderRadius.DEFAULT)] tw:data-[slot=avatar]:*:[--ring-opacity:10%] tw:sm:data-[slot=avatar]:*:size-6',
    // Hover
    'tw:data-[hover]:bg-zinc-950/5 tw:data-[slot=icon]:*:data-[hover]:fill-zinc-950',
    // Active
    'tw:data-[active]:bg-zinc-950/5 tw:data-[slot=icon]:*:data-[active]:fill-zinc-950',
    // Dark mode
    'tw:dark:text-brand-green-1 tw:dark:data-[slot=icon]:*:fill-zinc-400',
    'tw:dark:data-[hover]:bg-white/5 tw:dark:data-[slot=icon]:*:data-[hover]:fill-white',
    'tw:dark:data-[active]:bg-white/5 tw:dark:data-[slot=icon]:*:data-[active]:fill-white'
  );

  return (
    <span className={clsx(className, 'tw:relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="tw:absolute tw:inset-x-2 tw:-bottom-2.5 tw:h-0.5 tw:rounded-full tw:bg-brand-green-0 tw:dark:bg-brand-green-1"
        />
      )}
      {'href' in props ? (
        <Link
          {...props}
          className={classes}
          data-current={current ? 'true' : undefined}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          <TouchTarget>{children}</TouchTarget>
        </Link>
      ) : (
        <Headless.Button
          {...props}
          className={clsx('tw:cursor-default', classes)}
          data-current={current ? 'true' : undefined}
          ref={ref}
        >
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      )}
    </span>
  );
});

export function NavbarLabel({ className, ...props }: NavbarLabelProps) {
  return <span {...props} className={clsx(className, 'tw:truncate')} />;
}
